/**
 * Transaction templates service.
 *
 * GET /api/v1/templates/suggested?state=&side= (API_ENDPOINTS.suggestedTemplates) —
 * Rialty-maintained, read-only templates: { id, name, state, side,
 * folders[{slug,name,order}], items[{key,name,folder,mandatory,type,form,condition}],
 * parties[ParticipantRole] }.
 */
import { apiClient, API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { getFirebaseToken } from '../utils/getFirebaseToken';

const authHeaders = async () => ({ Authorization: `Bearer ${await getFirebaseToken()}` });

export const transactionTemplatesService = {
  getSuggestedTemplates: async ({ state, side } = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.suggestedTemplates, {
      headers: await authHeaders(),
      params: { state: state || undefined, side: side || undefined },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  // Personal templates — the caller's own; GET /templates/{id} also returns Rialty templates.
  getPersonalTemplates: async () => {
    const response = await apiClient.get(API_ENDPOINTS.personalTemplates, { headers: await authHeaders() });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Custom templates usable for a new transaction, grouped by owner:
   * MINE, BROKER, BROKERAGE (inherited) and PRIMARY_AGENT (the TC's selected agent).
   */
  getAvailableTemplates: async ({ state, side, primaryAgentId } = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.availableTemplates, {
      headers: await authHeaders(),
      params: { state: state || undefined, side: side || undefined, primaryAgentId: primaryAgentId || undefined },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /** primaryAgentId lets a TC open the selected agent's template (preview). */
  getTemplate: async (templateId, { primaryAgentId } = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.template(templateId), {
      headers: await authHeaders(),
      params: { primaryAgentId: primaryAgentId || undefined },
    });
    return response.data;
  },

  createPersonalTemplate: async (template) => {
    const response = await apiClient.post(API_ENDPOINTS.personalTemplates, template, { headers: await authHeaders() });
    return response.data;
  },

  updatePersonalTemplate: async (templateId, template) => {
    const response = await apiClient.put(API_ENDPOINTS.template(templateId), template, { headers: await authHeaders() });
    return response.data;
  },

  deletePersonalTemplate: async (templateId) => {
    await apiClient.delete(API_ENDPOINTS.template(templateId), { headers: await authHeaders() });
  },

  /**
   * Forms an agent can put in a template, as [{ code, name, form }] where `form` is the
   * template FormRef. CA reads the agent's own Lone Wolf CAR library (409 when Lone Wolf
   * isn't connected); CO reads the forms-service catalog.
   */
  getFormLibrary: async (state) => {
    const headers = await authHeaders();
    if (state === 'CO') {
      const response = await apiClient.get(API_ENDPOINTS.formsCatalog('CO'), { headers });
      const catalog = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      return catalog.map((f) => ({
        code: f.formId,
        name: f.formName,
        form: { source: 'FORMS_SERVICE', code: f.formId },
      }));
    }
    const libResp = await apiClient.get(API_ENDPOINTS.lonewolfLibraryByAcronym('CAR'), { headers });
    const libId = libResp.data?.libId;
    if (!libId) return [];
    const formsResp = await apiClient.get(API_ENDPOINTS.lonewolfLibraryForms(libId), { headers });
    return (Array.isArray(formsResp.data) ? formsResp.data : [])
      .map((f) => {
        const code = f.activeFormVersion?.acronym || f.acronym || '';
        return { code, name: f.activeFormVersion?.name || f.name || code, form: { source: 'LONEWOLF', library: 'CAR', code } };
      })
      .filter((f) => f.code)
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  },

  /** Retry the template forms auto-add (CA Lone Wolf / CO forms-service). Returns { added, failed }. */
  attachTemplateForms: async (transactionId) => {
    const response = await apiClient.post(`${API_ENDPOINTS.checklistItems(transactionId)}/attach-forms`, {}, { headers: await authHeaders() });
    return response.data || {};
  },

  /** Apply Template on an existing transaction (additive). Returns the added/skipped counts. */
  applyTemplate: async (transactionId, { templateId, saleType }) => {
    const response = await apiClient.post(
      `${API_BASE_URL}/api/v1/transactions/${transactionId}/template/apply`,
      { templateId, saleType: saleType || undefined },
      { headers: await authHeaders() },
    );
    return response.data || {};
  },

  getChecklistItems: async (transactionId) => {
    const response = await apiClient.get(API_ENDPOINTS.checklistItems(transactionId), {
      headers: await authHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  // link = { documentId, fileName } for an UPLOAD item's file; omitted for forms.
  markChecklistItemUploaded: async (transactionId, itemId, link = {}) => {
    const response = await apiClient.post(API_ENDPOINTS.checklistItemMarkUploaded(transactionId, itemId), link, {
      headers: await authHeaders(),
    });
    return response.data;
  },

  getSuggestedTemplate: async (templateId) => {
    const response = await apiClient.get(API_ENDPOINTS.suggestedTemplate(templateId), {
      headers: await authHeaders(),
    });
    return response.data;
  },
};
